import React, { useEffect, useState, useContext } from "react";
import { MSALContext } from "../MSALContext";
import { getAuthHeaders } from "../Auth/authHelpers";
import "./TrainersPage.css";
import "bootstrap/dist/css/bootstrap.min.css"; // Import Bootstrap CSS

const TrainersPage = () => {
	const { msalInstance } = useContext(MSALContext);
	const [trainers, setTrainers] = useState([]);
	const [loading, setLoading] = useState(true); // Add loading state
	const [error, setError] = useState(null);
	const [selectedSite, setSelectedSite] = useState("All Sites");
	const [siteIDs, setSiteIDs] = useState([]);

	useEffect(() => {
		const fetchTrainers = async () => {
			// Ensure msalInstance is available
			if (!msalInstance) {
				setError("MSAL instance is not available.");
				setLoading(false);
				return;
			}

			const accounts = msalInstance.getAllAccounts();
			if (accounts.length > 0) {
				const username = accounts[0].username;
				try {
					const response = await fetch(
						`${process.env.REACT_APP_API_URL}/permissions/trainers/${encodeURIComponent(
							username,
						)}`,
						{
							headers: await getAuthHeaders(username),
						},
					);

					if (!response.ok) {
						const text = await response.text();
						throw new Error(text);
					}

					const data = await response.json();

					// Normalize the data to handle multiple SiteIDs
					const normalizedTrainers = data.map((trainer) => ({
						...trainer,
						SiteID: Array.isArray(trainer.SiteID)
							? trainer.SiteID // Already an array
							: trainer.SiteID.split(",").map((site) => site.trim()), // Normalize to array
					}));
					setTrainers(normalizedTrainers);

					// Extract unique SiteIDs from the data
					const uniqueSiteIDs = Array.from(
						new Set(
							normalizedTrainers
								.flatMap((trainer) => trainer.SiteID) // Flatten all SiteID arrays
								.filter((site) => site) // Remove empty or null SiteIDs
						)
					);
					setSiteIDs(["All Sites", ...uniqueSiteIDs]);
				} catch (error) {
					console.error("Error fetching trainers:", error);
					setError(error.message);
				} finally {
					setLoading(false); // Set loading to false after data is fetched
				}
			} else {
				setError("No accounts found in MSAL instance.");
				setLoading(false);
			}
		};

		fetchTrainers();
	}, [msalInstance]);

	// Filter trainers based on the selected site
	const filteredTrainers =
		selectedSite === "All Sites"
			? trainers
			: trainers.filter((trainer) => trainer.SiteID.includes(selectedSite));

	return (
		<div className="trainers-container container">
			<div className="d-flex justify-content-between align-items-center mb-3">
				<h1 className="my-0">Available Trainers Per Site</h1>
				<div className="trainers-filter-container">
					<label htmlFor="trainers-site-select" className="mr-2">
						Filter by SiteID:
					</label>
					<select
						id="trainers-site-select"
						className="form-control"
						value={selectedSite}
						onChange={(e) => setSelectedSite(e.target.value)}
						style={{ maxWidth: "300px" }}
					>
						{siteIDs.map((siteID) => (
							<option key={siteID} value={siteID}>
								{siteID}
							</option>
						))}
					</select>
				</div>
			</div>
			{error && <p className="trainers-error text-danger">{error}</p>}
			{loading ? (
				<div className="trainers-loading-container">
					<img
						src="/images/loadinggears.gif"
						alt="Loading..."
						className="trainers-loading-image"
					/>
				</div>
			) : (
				<div className="table-responsive trainers-table-container">
					<table className="table table-bordered table-hover trainers-table">
						<thead className="thead-dark">
							<tr>
								<th>CW AD ID</th>
								<th>Site ID</th>
								<th>Site Node</th>
								<th>Business Title</th>
								<th>Name</th>
							</tr>
						</thead>
						<tbody>
							{filteredTrainers.map((trainer, index) => (
								<tr key={`${trainer.TrainerID}-${index}`}>
									<td>{trainer.TrainerID}</td>
									<td>
										{Array.isArray(trainer.SiteID)
											? trainer.SiteID.join(", ")
											: trainer.SiteID}
									</td>
									<td>{trainer.site_node}</td>
									<td>{trainer.business_title}</td>
									<td>{trainer.user_name}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default TrainersPage;
